import { useEffect, useState, useRef } from "react";
import { useParams, NavLink } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Camera, X, ArrowLeft, Save } from "lucide-react";

export default function BendeEdit() {
  const { slug } = useParams();
  const [member, setMember] = useState(null);
  const [tagline, setTagline] = useState("");
  const [message, setMessage] = useState("");
  const [coverId, setCoverId] = useState(null);
  const [photoIds, setPhotoIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [adminReady, setAdminReady] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const coverRef = useRef(null);
  const photosRef = useRef(null);

  useEffect(() => {
    api.get("/admin/verify")
      .then(() => setAdminReady(true))
      .catch(() => setAdminReady(false))
      .finally(() => setCheckingAdmin(false));
  }, []);

  const load = () => {
    api.get(`/bende/${slug}`)
      .then((r) => {
        setMember(r.data);
        setTagline(r.data.tagline || "");
        setMessage(r.data.message || "");
        setCoverId(r.data.cover_photo_id || null);
        setPhotoIds(r.data.photo_ids || []);
      })
      .catch(() => setNotFound(true));
  };

  useEffect(() => { load(); }, [slug]);

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.id;
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    try {
      const id = await uploadPhoto(file);
      setCoverId(id);
      toast.success("Coverfoto geladen — vergeet niet op te slaan");
    } catch { toast.error("Uploaden mislukt"); }
  };

  const handleExtraPhotos = async (files) => {
    try {
      const ids = [];
      for (const f of files) {
        ids.push(await uploadPhoto(f));
      }
      setPhotoIds((prev) => [...prev, ...ids]);
      toast.success(`${ids.length} foto('s) toegevoegd`);
    } catch { toast.error("Uploaden mislukt"); }
  };

  const removePhoto = (id) => setPhotoIds((prev) => prev.filter((x) => x !== id));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/bende/${slug}`, {
        tagline,
        message,
        cover_photo_id: coverId,
        photo_ids: photoIds,
      });
      toast.success("Bewaard! Bedankt om jullie pagina in te vullen ♥");
    } catch { toast.error("Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="font-script text-5xl text-[#006A71]">Niet gevonden</h1>
        <p className="text-[#4A6165] mt-4">Deze bewerk-link bestaat niet. Controleer de URL of vraag hem opnieuw op.</p>
      </div>
    );
  }
  if (!member) return <div className="p-10 text-center font-script text-3xl">Laden...</div>;

  if (checkingAdmin) return <div className="p-10 text-center font-script text-3xl">Laden...</div>;

  if (!adminReady) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="bende-edit-admin-only">
        <NavLink to="/de-bende" className="text-[10px] tracking-[0.3em] uppercase text-[#4A6165] inline-flex items-center gap-2 hover:text-[#006A71]">
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar de bende
        </NavLink>
        <h1 className="font-script text-6xl text-[#006A71] mt-12">Admin login vereist</h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <p className="font-serif italic text-lg text-[#4A6165] mt-4">
          Deze bende-bewerkpagina is alleen toegankelijk voor ingelogde admins.
        </p>
        <NavLink to="/admin" className="btn-primary mt-10 inline-flex">Naar admin login</NavLink>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="bende-edit-page">
      <NavLink to="/de-bende" className="text-[10px] tracking-[0.3em] uppercase text-[#4A6165] inline-flex items-center gap-2 hover:text-[#006A71]">
        <ArrowLeft className="w-3.5 h-3.5" /> Terug naar de bende
      </NavLink>

      <div className="text-center mt-8 mb-10">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#006A71]">Vul jullie pagina in</p>
        <h1 className="font-script text-6xl text-[#006A71] mt-3">{member.names}</h1>
        <div className="section-divider"><span className="section-divider-dot" /></div>
        <p className="font-serif italic text-[#4A6165] mt-4">
          Personaliseer jullie kaartje in "De Bende": kies een coverfoto, pas de boodschap aan en voeg extra foto's toe.
        </p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={save}
        className="bg-white border border-[#006A71]/15 rounded-sm p-8 sm:p-12 brutal-shadow-lg space-y-10"
      >
        {/* COVER */}
        <div>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Coverfoto</span>
          <p className="text-xs text-[#4A6165] mt-1 font-serif italic">Dit is de foto die op de "De Bende" pagina getoond wordt.</p>
          <div className="mt-4 flex items-start gap-4">
            <div className="w-40 h-52 border border-[#006A71]/20 bg-[#E8DFCF] flex items-center justify-center overflow-hidden">
              {coverId ? (
                <img src={photoUrl(coverId)} alt="cover" className="w-full h-full object-cover" data-testid="cover-preview" />
              ) : (
                <span className="font-script text-4xl text-[#006A71]">{member.names.split(" ")[0]}</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button" onClick={() => coverRef.current?.click()}
                className="btn-secondary" data-testid="cover-upload-btn"
              >
                <Camera className="w-3.5 h-3.5" /> {coverId ? "Vervang cover" : "Kies cover"}
              </button>
              {coverId && (
                <button
                  type="button" onClick={() => setCoverId(null)}
                  className="text-[10px] tracking-[0.3em] uppercase text-red-700 hover:underline"
                >
                  Verwijder cover
                </button>
              )}
              <input ref={coverRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => handleCoverUpload(e.target.files?.[0])} />
            </div>
          </div>
        </div>

        {/* TAGLINE */}
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Ondertitel</span>
          <p className="text-xs text-[#4A6165] mt-1 font-serif italic">Een korte, speelse omschrijving van jullie koppel.</p>
          <input value={tagline} onChange={(e) => setTagline(e.target.value)}
            className="scrap-input mt-3" placeholder="Bv. De vroege vogels van de bende"
            data-testid="edit-tagline-input" />
        </label>

        {/* MESSAGE */}
        <label className="block">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Jullie boodschap</span>
          <p className="text-xs text-[#4A6165] mt-1 font-serif italic">Een persoonlijke tekst voor Lien & Gust. Zoveel of zo weinig als je wil.</p>
          <textarea rows={10} value={message} onChange={(e) => setMessage(e.target.value)}
            className="scrap-input mt-3 resize-none" placeholder="Lieve Lien en Gust..."
            data-testid="edit-message-input" />
        </label>

        {/* EXTRA PHOTOS */}
        <div>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#006A71]">Extra foto's (optioneel)</span>
          <p className="text-xs text-[#4A6165] mt-1 font-serif italic">Deze foto's verschijnen onderaan jullie boodschap-pagina.</p>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
            {photoIds.map((pid) => (
              <div key={pid} className="relative aspect-square border border-[#006A71]/20">
                <img src={photoUrl(pid)} className="w-full h-full object-cover" alt="" />
                <button
                  type="button" onClick={() => removePhoto(pid)}
                  className="absolute -top-2 -right-2 bg-[#FAF6EF] border border-[#006A71] text-[#006A71] rounded-full w-6 h-6 flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button
              type="button" onClick={() => photosRef.current?.click()}
              className="aspect-square border border-dashed border-[#006A71]/40 flex items-center justify-center text-[#006A71] hover:bg-[#E8DFCF]/30 transition-colors"
              data-testid="extra-photos-btn"
            >
              <Camera className="w-5 h-5" />
            </button>
            <input ref={photosRef} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => handleExtraPhotos(Array.from(e.target.files || []))} />
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-btn">
          <Save className="w-3.5 h-3.5" /> {saving ? "Opslaan..." : "Opslaan"}
        </button>
      </motion.form>

      <p className="text-center text-[10px] tracking-[0.3em] uppercase text-[#4A6165] mt-8">
        Alle wijzigingen zijn meteen zichtbaar op <NavLink to={`/de-bende/${slug}`} className="underline">jullie pagina</NavLink>.
      </p>
    </div>
  );
}
